import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { MarketingTarotCard } from './MarketingTarotCard';
import { MARKETING_TAROT_DECK } from '@/data/marketingTarot';
import { PlanModal } from './PlanModal';
import { trackConversion } from '@/lib/analytics';
import { recordReading } from '@/lib/streak';
import { mdBold } from '@/lib/sanitize';
import { toast } from 'sonner';
import { ArrowLeft, Sparkles, Calendar, Star, Loader2, Share2, Copy, Linkedin, Twitter, CheckCircle } from 'lucide-react';

// Card position definitions
const CARD_POSITIONS = [
  { key: 'card1', label: 'Core Force', number: 1 },
  { key: 'card2', label: 'Hidden Influence', number: 2 },
  { key: 'card3', label: 'Recommended Action', number: 3 },
];

export const ReadingResult = ({ isOpen, readingData, onClose, userName }) => {
  const [revealedCards, setRevealedCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [spreadCards, setSpreadCards] = useState([]);
  const [aiReading, setAiReading] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analyzedContext, setAnalyzedContext] = useState(null);
  
  // Save & Share state
  const [readingId, setReadingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Plan state
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planData, setPlanData] = useState(null);
  const [isPlanLoading, setIsPlanLoading] = useState(false);

  useEffect(() => {
    if (isOpen && readingData) {
      // Randomly select 3 cards for the spread
      const shuffled = [...MARKETING_TAROT_DECK].sort(() => Math.random() - 0.5);
      const selected = shuffled.slice(0, 3);
      setSpreadCards(selected);
      setRevealedCards([]);
      setFlippedCards([]);
      setAiReading(null);
      setError(null);
      setReadingId(null);
      setPlanData(null);
      setAnalyzedContext(null);

      // Phase 1: Deal cards face-down one by one
      selected.forEach((_, index) => {
        setTimeout(() => {
          setRevealedCards(prev => [...prev, index]);
        }, (index + 1) * 800);
      });

      // Phase 2: Flip each card face-up after it lands
      selected.forEach((_, index) => {
        setTimeout(() => {
          setFlippedCards(prev => [...prev, index]);
        }, (index + 1) * 800 + 1000);
      });

      // After cards are revealed, fetch AI reading
      setTimeout(() => {
        fetchAiReading(selected, readingData);
      }, 3000);
    }
  }, [isOpen, readingData]);

  const fetchAiReading = async (cards, data) => {
    setIsLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/reading/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          goal: data.goal,
          audience: data.audience,
          industry: data.industry,
          company_size: data.companySize,
          segment: data.segment,
          challenge: data.challenge,
          website_url: data.websiteUrl || null,
          user_name: userName || null,
          cards: cards.map((card, index) => ({
            name: card.name,
            position: CARD_POSITIONS[index].key,
            position_label: CARD_POSITIONS[index].label,
          })),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate reading');
      }

      const result = await response.json();
      setAiReading(result.reading);
      trackConversion('generate_reading');

      const streak = recordReading();
      if (streak.extended) {
        setTimeout(() => {
          toast.success(
            streak.count > 1 ? `🔥 ${streak.count}-month reading streak!` : '✨ Reading streak started!',
            {
              description: streak.count > 1
                ? 'The cards reward the consistent. Keep returning each month.'
                : 'Return next month to keep your streak alive.',
              duration: 5000,
            }
          );
        }, 1500);
      }
      
      // Store the auto-analyzed context from backend
      if (result.context) {
        setAnalyzedContext(result.context);
      }

      // Reading is auto-saved on the backend, use the returned reading_id
      if (result.reading_id) {
        setReadingId(result.reading_id);
      }
    } catch (err) {
      console.error('Error fetching AI reading:', err);
      setError('Failed to generate AI reading. Showing default interpretation.');
    } finally {
      setIsLoading(false);
    }
  };

  // saveReading is now handled automatically by the generate endpoint
  // This function is kept for potential manual re-sharing scenarios
  const saveReading = async (cards, reading, data) => {
    setIsSaving(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/reading/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          goal: data.goal,
          audience: data.audience,
          industry: data.industry,
          company_size: data.companySize,
          segment: data.segment,
          challenge: data.challenge,
          cards: cards.map((card, index) => ({
            name: card.name,
            position: CARD_POSITIONS[index].key,
            position_label: CARD_POSITIONS[index].label,
          })),
          reading: reading,
        }),
      });
      
      if (response.ok) {
        const result = await response.json();
        setReadingId(result.reading_id);
      }
    } catch (err) {
      console.error('Error saving reading:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleGetPlan = async () => {
    setShowPlanModal(true);
    setIsPlanLoading(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/plan/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: '', // Will be provided in modal
          reading_id: readingId || '',
          goal: readingData.goal,
          audience: readingData.audience,
          industry: readingData.industry,
          company_size: readingData.companySize,
          segment: readingData.segment,
          challenge: readingData.challenge,
          cards: spreadCards.map((card, index) => ({
            name: card.name,
            position: CARD_POSITIONS[index].key,
            position_label: CARD_POSITIONS[index].label,
          })),
          reading: aiReading,
        }),
      });
      
      if (response.ok) {
        const result = await response.json();
        setPlanData(result.plan);
        trackConversion('generate_plan');
      }
    } catch (err) {
      console.error('Error generating plan:', err);
    } finally {
      setIsPlanLoading(false);
    }
  };

  const handleSendPlan = async (email) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const res = await fetch(`${backendUrl}/api/plan/email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        goal: readingData.goal,
        audience: readingData.audience,
        industry: readingData.industry,
        plan: planData,
      }),
    });
    if (!res.ok) throw new Error('Failed to send email');
    trackConversion('plan_email');
  };

  const handleDownloadPdf = async () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const res = await fetch(`${backendUrl}/api/plan/download`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        goal: readingData.goal,
        audience: readingData.audience,
        industry: readingData.industry,
        plan: planData,
      }),
    });
    if (!res.ok) throw new Error('Failed to download PDF');
    trackConversion('plan_download');
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = '90-Day-Marketing-Plan.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const shareUrl = typeof window !== 'undefined' && readingId
    ? `${window.location.origin}/api/share/r/${readingId}`
    : '';
  
  const shareImageUrl = readingId 
    ? `${process.env.REACT_APP_BACKEND_URL}/api/reading/${readingId}/image`
    : '';

  const handleCopyLink = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShareLinkedIn = () => {
    if (shareUrl) {
      const title = "Here's what the marketing gods have predicted 🔮";
      const summary = `My Marketing Tarot Reading revealed: ${spreadCards.map(c => c.name).join(', ')}. Get your own reading!`;
      // LinkedIn sharing with custom text
      window.open(
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
        '_blank'
      );
    }
  };

  const handleShareTwitter = () => {
    if (shareUrl) {
      const cardNames = spreadCards.map(c => c.name.replace('The ', '')).join(', ');
      const text = `🔮 Here's what the marketing gods have predicted!\n\nMy cards: ${cardNames}\n\nGet your own Marketing Tarot reading:`;
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
    }
  };

  if (!isOpen || !readingData) return null;

  // Merge user-provided and auto-analyzed context
  const context = {
    goal: analyzedContext?.goal || readingData.goal,
    audience: analyzedContext?.audience || readingData.audience,
    industry: analyzedContext?.industry || readingData.industry,
    companySize: analyzedContext?.company_size || readingData.companySize,
    segment: analyzedContext?.segment || readingData.segment,
    challenge: analyzedContext?.challenge || readingData.challenge,
    websiteUrl: analyzedContext?.website_url || readingData.websiteUrl,
  };

  // Get interpretation for a card (AI or fallback)
  const getCardInterpretation = (index) => {
    if (aiReading) {
      const key = `card${index + 1}`;
      return aiReading[key] || null;
    }
    return null;
  };

  return (
    <div className="fixed inset-0 z-[100] bg-gradient-to-br from-[#0a1628] via-[#0f1f35] to-[#0a1628] overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0a1628]/90 backdrop-blur-sm border-b border-[#d4c4a8]/20">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            onClick={onClose}
            variant="ghost"
            className="text-[#d4c4a8] hover:text-[#e8dcc8] hover:bg-[#d4c4a8]/10"
            data-testid="reading-result-back"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Deck
          </Button>
          
          {/* Share buttons */}
          {readingId && (
            <div className="flex items-center gap-2">
              <span className="text-[#d4c4a8]/50 text-sm hidden sm:inline">Share:</span>
              <Button
                onClick={handleCopyLink}
                variant="ghost"
                size="sm"
                className="text-[#d4c4a8] hover:text-[#e8dcc8]"
                data-testid="copy-link-btn"
              >
                {copied ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </Button>
              <Button
                onClick={handleShareLinkedIn}
                variant="ghost"
                size="sm"
                className="text-[#d4c4a8] hover:text-[#0A66C2]"
              >
                <Linkedin className="w-4 h-4" />
              </Button>
              <Button
                onClick={handleShareTwitter}
                variant="ghost"
                size="sm"
                className="text-[#d4c4a8] hover:text-[#1DA1F2]"
              >
                <Twitter className="w-4 h-4" />
              </Button>
            </div>
          )}
          
          {!readingId && <div className="w-32" />}
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        
        {/* Title */}
        <div className="text-center mb-10">
          <h1 className="font-display text-3xl md:text-4xl text-[#e8dcc8] mb-4">
            Your Marketing Tarot Reading
          </h1>
          <p className="font-body text-[#d4c4a8]/70">
            Here's what the cards reveal for you
          </p>
        </div>

        {/* Context Summary */}
        <div className={cn(
          "mb-12 p-6 rounded-xl",
          "bg-[#1e2a4a]/40 border border-[#d4c4a8]/20"
        )}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 font-body text-sm">
            {context.goal && (
              <p><span className="text-[#e89b47]">Your Question:</span> <span className="text-[#e8dcc8]">{context.goal}</span></p>
            )}
            {context.websiteUrl && (
              <p><span className="text-[#e89b47]">Website:</span> <span className="text-[#e8dcc8]">{context.websiteUrl}</span></p>
            )}
            {context.audience && (
              <p><span className="text-[#e89b47]">Audience:</span> <span className="text-[#e8dcc8]">{context.audience}</span></p>
            )}
            {context.industry && (
              <p><span className="text-[#e89b47]">Industry:</span> <span className="text-[#e8dcc8]">{context.industry}</span></p>
            )}
            {context.companySize && (
              <p><span className="text-[#e89b47]">Company Size:</span> <span className="text-[#e8dcc8]">{context.companySize}</span></p>
            )}
            {context.segment && (
              <p><span className="text-[#e89b47]">Segment:</span> <span className="text-[#e8dcc8]">{context.segment}</span></p>
            )}
          </div>
          {analyzedContext && (analyzedContext.audience || analyzedContext.industry) && (
            <p className="text-[#d4c4a8]/40 text-xs mt-3 font-body">
              Company details auto-analyzed from your website
            </p>
          )}
        </div>

        {/* Animated Deck Deal */}
        <div className="mb-12 py-8">
          <div className="relative flex items-center justify-center" style={{ minHeight: '260px' }}>
            {/* Deck stack (center) */}
            <div className="absolute" style={{ left: '50%', transform: 'translateX(-50%)', zIndex: 1 }}>
              {[...Array(Math.max(0, 3 - revealedCards.length))].map((_, i) => (
                <div
                  key={`deck-${i}`}
                  className="absolute w-[100px] h-[150px] rounded-xl border-2 border-[#e89b47]/30 bg-gradient-to-br from-[#1a2d50] via-[#162340] to-[#0f1a30] shadow-lg"
                  style={{
                    top: `${-i * 2}px`,
                    left: `${-i * 1}px`,
                    zIndex: 10 - i,
                  }}
                >
                  <div className="w-full h-full flex items-center justify-center rounded-lg">
                    <div className="w-[72px] h-[120px] border border-[#e89b47]/20 rounded-lg flex items-center justify-center">
                      <div className="w-6 h-6 border border-[#e89b47]/30 rounded-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-[#e89b47]/40 rounded-full" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Dealt cards flying to positions */}
            <div className="flex items-start justify-center gap-8 md:gap-16 w-full relative" style={{ zIndex: 20 }}>
              {CARD_POSITIONS.map((pos, index) => {
                const card = spreadCards[index];
                const isDealt = revealedCards.includes(index);
                const isFlipped = flippedCards.includes(index);
                return (
                  <div
                    key={pos.key}
                    className="flex flex-col items-center w-[140px]"
                    style={{
                      transition: 'opacity 0.6s, transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      transitionDelay: `${index * 800}ms`,
                      opacity: isDealt ? 1 : 0,
                      transform: isDealt 
                        ? 'translateY(0) scale(1) rotate(0deg)' 
                        : 'translateY(-40px) scale(0.5) rotate(10deg)',
                    }}
                  >
                    {card && (
                      <div className="relative group" style={{ perspective: '800px' }}>
                        {/* Glow on flip */}
                        <div 
                          className="absolute -inset-2 rounded-2xl transition-opacity duration-700"
                          style={{
                            background: 'radial-gradient(circle, rgba(232,155,71,0.2) 0%, transparent 70%)',
                            opacity: isFlipped ? 1 : 0,
                          }}
                        />
                        {/* Flip container */}
                        <div
                          style={{
                            transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                            transformStyle: 'preserve-3d',
                            transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                          }}
                        >
                          {/* Back face (face-down) */}
                          <div
                            style={{
                              backfaceVisibility: 'hidden',
                              position: isFlipped ? 'absolute' : 'relative',
                              top: 0, left: 0, width: '100%', height: '100%',
                            }}
                          >
                            <div className="w-[120px] h-[180px] rounded-xl border-2 border-[#e89b47]/40 bg-gradient-to-br from-[#1a2d50] via-[#162340] to-[#0f1a30] shadow-lg">
                              <div className="w-full h-full flex items-center justify-center rounded-lg">
                                <div className="w-[90px] h-[150px] border border-[#e89b47]/20 rounded-lg flex items-center justify-center">
                                  <div className="w-8 h-8 border border-[#e89b47]/30 rounded-full flex items-center justify-center">
                                    <div className="w-3 h-3 bg-[#e89b47]/40 rounded-full" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          {/* Front face (revealed card) */}
                          <div
                            style={{
                              backfaceVisibility: 'hidden',
                              transform: 'rotateY(180deg)',
                            }}
                          >
                            <MarketingTarotCard
                              card={card}
                              isFlipped={true}
                              size="small"
                              isHoverable={false}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    <p className={cn(
                      "mt-3 text-[#d4c4a8]/60 text-xs font-body transition-opacity duration-500",
                      isFlipped ? "opacity-100" : "opacity-0"
                    )}>
                      Card {pos.number} — {pos.label}
                    </p>
                    <p className={cn(
                      "font-display text-[#e89b47] text-sm italic transition-opacity duration-500",
                      isFlipped ? "opacity-100" : "opacity-0"
                    )}>
                      {card?.name || '...'}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-12 mb-8">
            <Loader2 className="w-12 h-12 text-[#e89b47] animate-spin mb-4" />
            <p className="font-display text-[#e8dcc8] text-lg">Consulting the cards...</p>
            <p className="font-body text-[#d4c4a8]/60 text-sm">Generating your personalized reading</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="mb-8 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-center">
            <p className="font-body text-red-400">{error}</p>
          </div>
        )}

        {/* Individual Card Readings */}
        {!isLoading && spreadCards.length > 0 && (
          <div className="space-y-12 mb-16">
            {spreadCards.map((card, index) => {
              const position = CARD_POSITIONS[index];
              const interp = getCardInterpretation(index);
              const isRevealed = revealedCards.includes(index);
              
              return (
                <div
                  key={card.id}
                  className={cn(
                    "transition-all duration-700",
                    isRevealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                  )}
                  style={{ transitionDelay: `${index * 300}ms` }}
                >
                  {/* Section Header */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex-shrink-0">
                      <MarketingTarotCard
                        card={card}
                        isFlipped={isRevealed}
                        size="small"
                        isHoverable={false}
                      />
                    </div>
                    <div>
                      <p className="text-[#d4c4a8]/60 text-sm">Card {position.number}</p>
                      <h2 className="font-display text-2xl text-[#e8dcc8]">
                        ✨ {card.name} — <span className="text-[#e89b47]">{position.label}</span>
                      </h2>
                    </div>
                  </div>

                  {/* Interpretation */}
                  {interp && (
                    <div className={cn(
                      "ml-0 md:ml-36 p-6 rounded-xl",
                      "bg-[#1e2a4a]/40 border border-[#d4c4a8]/10"
                    )}>
                      <p className="font-display text-lg text-[#e8dcc8] mb-4">
                        {interp.title}
                      </p>
                      <p className="font-body text-[#d4c4a8]/80 leading-relaxed mb-4"
                         dangerouslySetInnerHTML={{ __html: mdBold(interp.body, 'text-[#e8dcc8]') }}
                      />
                      <p className="font-body text-[#e89b47] italic border-l-2 border-[#e89b47]/50 pl-4">
                        <strong>Interpretation:</strong> {interp.interpretation}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Overall Reading */}
        {aiReading?.overall && !isLoading && (
          <div className={cn(
            "mb-12 p-8 rounded-xl",
            "bg-gradient-to-br from-[#1e2a4a]/80 to-[#2a1f42]/60",
            "border-2 border-[#e89b47]/40",
            "animate-fade-in"
          )}>
            <h2 className="font-display text-2xl text-[#e8dcc8] mb-6 flex items-center gap-3">
              <Star className="w-6 h-6 text-[#e89b47]" />
              🌟 The Overall Reading
            </h2>
            
            <p className="font-body text-[#d4c4a8]/90 text-lg mb-6"
               dangerouslySetInnerHTML={{ __html: mdBold(aiReading.overall.intro, 'text-[#e89b47]') }}
            />
            
            <p className="font-body text-[#e8dcc8] mb-4">
              Your {context.goal?.toLowerCase() || 'visibility'} will grow fastest when you:
            </p>
            
            <ul className="space-y-2 mb-6">
              {aiReading.overall.bullets?.map((bullet, i) => (
                <li key={i} className="flex items-start gap-3 font-body text-[#d4c4a8]/80">
                  <span className="text-[#e89b47] mt-1">•</span>
                  <span dangerouslySetInnerHTML={{ __html: mdBold(bullet, 'text-[#e8dcc8]') }} />
                </li>
              ))}
            </ul>
            
            <p className="font-body text-lg text-[#e8dcc8]"
               dangerouslySetInnerHTML={{ __html: mdBold(aiReading.overall.closing, 'text-[#e89b47]') }}
            />
          </div>
        )}

        {/* CTA */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 text-[#d4c4a8]/30 text-sm mb-8">
            <span>✦</span>
            <span className="font-body">May your marketing strategy be guided by these insights</span>
            <span>✦</span>
          </div>
          
          <a
            href="https://calendly.com/marketingtarot/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#e89b47] to-[#d4883a] text-[#0a1628] hover:from-[#e89b47]/90 hover:to-[#d4883a]/90 font-display px-10 py-4 text-lg rounded-xl shadow-lg shadow-[#e89b47]/20 transition-all hover:scale-105 hover:shadow-xl hover:shadow-[#e89b47]/30"
            data-testid="start-journey-btn"
          >
            <Sparkles className="w-5 h-5" />
            Start Your Journey
          </a>
          
          <p className="font-body text-[#d4c4a8]/50 text-sm mt-4">
            Book a clarity call and turn these insights into action
          </p>
        </div>
      </div>
      
      {/* Plan Modal */}
      <PlanModal
        isOpen={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        planData={planData}
        isLoading={isPlanLoading}
        onDownloadPdf={handleDownloadPdf}
        onSendEmail={handleSendPlan}
        readingData={readingData}
      />
    </div>
  );
};

export default ReadingResult;
